/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState } from 'react';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { useQuery } from 'react-query';
import { Badge, Card, Empty, Spin, Tag, Tooltip } from 'antd';
import { useIntl } from 'react-intl';
import { getContractOffersCore } from '~src/services/api/offers';
import { getOfferStatusVisual } from '~src/utils/shareUtils/useShareKeysStatusUtils';
import {
  UserOutlined,
  MailOutlined,
  CalendarOutlined,
  EditOutlined,
} from '@ant-design/icons';

type Props = {
  projectId: number | undefined;
  tenderId: string | number | undefined;
};

export const OffersRightCore: React.FC<Props> = ({ projectId, tenderId }) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const { data, isLoading } = useQuery(
    ['offers-core', projectId, tenderId],
    () => getContractOffersCore({ projectId: projectId!, tenderId: tenderId! }),
    { enabled: !!projectId && !!tenderId }
  );

  const [expandedIds, setExpandedIds] = useState<Set<string | number>>(
    new Set()
  );

  const toggleExpanded = (id: string | number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isRejectedOffer = (o: any): boolean => Number(o?.state) === 5;

  const formatDate = (unixSeconds?: number) => {
    if (!unixSeconds) return '-';

    return intl.formatDate(unixSeconds * 1000, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getVoucherProgressColor = (responded: number, total: number) => {
    if (total === 0) return '#8c8c8c'; // Grey for no vouchers
    if (responded === total) return '#52c41a'; // Green for complete
    if (responded === 0) return '#ff4d4f'; // Red for none responded
    return '#faad14'; // Orange for partial
  };

  if (isLoading) return <Spin />;

  if (!data || !data.length) {
    return (
      <Empty
        description={intl.formatMessage({
          id: 'offers-core-empty',
        })}
      />
    );
  }

  return (
    <div css={styles.wrap}>
      <div css={styles.list}>
        {data.map((c) => {
          // Filter logic
          const offers = (c.offers ?? []).filter(
            (o: any) => !isRejectedOffer(o)
          );

          const submitted = c.summary?.submitted ?? 0;
          const total = offers.length;
          const hasOffers = total > 0;
          const isOpen = expandedIds.has(c.contract_id);

          return (
            <Card
              key={c.contract_id}
              size="small"
              css={styles.card}
              // Header logic
              headStyle={{ backgroundColor: '#f5f5f5' }}
              title={
                <div
                  css={styles.cardHeader}
                  // Clicking header toggles if offers exist
                  onClick={() => hasOffers && toggleExpanded(c.contract_id)}
                  style={{ cursor: hasOffers ? 'pointer' : 'default' }}>
                  {/* Left: Contract Name */}
                  <span css={styles.contractName}>
                    #
                    {c.contract_reference ||
                      intl.formatMessage({
                        id: 'no-reference',
                      })}{' '}
                    - {c.contract_name}
                  </span>

                  {/* Right: Badges */}
                  <div css={styles.chips}>
                    {/* Participation (Blue) */}
                    <div css={styles.badgeWrapper}>
                      <span css={styles.badgeLabel}>
                        {intl.formatMessage({
                          id: 'participation',
                        })}
                        :
                      </span>
                      <Badge
                        count={total}
                        showZero
                        style={{ backgroundColor: '#1677ff' }}
                      />
                    </div>

                    {/* Submitted (Green) */}
                    <div css={styles.badgeWrapper}>
                      <span css={styles.badgeLabel}>
                        {intl.formatMessage({
                          id: 'submitted',
                        })}
                        :
                      </span>
                      <Badge
                        count={submitted}
                        showZero
                        style={{ backgroundColor: '#52c41a' }}
                      />
                    </div>
                  </div>
                </div>
              }
              // Body styles for the "white space"
              bodyStyle={{ padding: '8px 12px' }}>
              {/* If NO offers: Show gray text */}
              {!hasOffers && (
                <div css={styles.hintTextDisabled}>
                  {intl.formatMessage({
                    id: 'no-offers-available',
                  })}
                </div>
              )}

              {/* If HAS offers but CLOSED: Show "Click to open" */}
              {hasOffers && !isOpen && (
                <div
                  css={styles.hintTextActive}
                  onClick={() => toggleExpanded(c.contract_id)}>
                  {intl.formatMessage({
                    id: 'click-to-open',
                  })}
                </div>
              )}

              {/* If HAS offers and OPEN: Show List */}
              {hasOffers && isOpen && (
                <div css={styles.offersList}>
                  {offers.map((o: any) => {
                    const title =
                      o.share_key_title ||
                      intl.formatMessage(
                        { id: 'offers-offer-number' },
                        { id: o.id }
                      );
                    const { color, text } = getOfferStatusVisual(intl, o);

                    return (
                      <div key={o.id} css={styles.offerItem}>
                        {/* Row 1: Title & State */}
                        <div css={styles.offerRow}>
                          <span css={styles.offerTitle}>{title}</span>
                          <Tooltip
                            title={intl.formatMessage(
                              { id: 'offers-state-label' },
                              { state: text }
                            )}>
                            <Tag color={color} style={{ margin: 0 }}>
                              {text}
                            </Tag>
                          </Tooltip>
                        </div>

                        {/* Row 2: Created By & Created At */}
                        <div css={styles.offerRow}>
                          <span css={styles.offerInfo}>
                            <UserOutlined rev={''} style={{ marginRight: 6 }} />
                            <span css={styles.label}>
                              {intl.formatMessage({
                                id: 'created-by',
                              })}
                              :
                            </span>{' '}
                            {o.offer_created_by_name || '-'}
                          </span>
                          <span css={styles.offerInfo}>
                            <CalendarOutlined
                              rev={''}
                              style={{ marginRight: 6 }}
                            />
                            {formatDate(o.offer_created_at)}
                          </span>
                        </div>

                        {/* Row 3: Updated By & Updated At */}
                        <div css={styles.offerRow}>
                          <span css={styles.offerInfo}>
                            <EditOutlined rev={''} style={{ marginRight: 6 }} />
                            <span css={styles.label}>
                              {intl.formatMessage({
                                id: 'updated-by',
                              })}
                              :
                            </span>{' '}
                            {o.offer_updated_by_name || '-'}
                          </span>
                          <span css={styles.offerInfo}>
                            <CalendarOutlined
                              rev={''}
                              style={{ marginRight: 6 }}
                            />
                            {formatDate(o.offer_updated_at)}
                          </span>
                        </div>

                        {/* Row 4: Email & Voucher Progress */}
                        <div css={styles.offerRow}>
                          <span css={styles.offerInfo}>
                            <MailOutlined rev={''} style={{ marginRight: 6 }} />
                            {o.offeror_identity_information?.email || '-'}
                          </span>

                          {o.voucher_response && (
                            <div css={styles.voucherProgressBadges}>
                              <Tooltip
                                title={intl.formatMessage({
                                  id: 'offers-core-voucher-progress-tooltip',
                                })}>
                                <Tag
                                  css={styles.voucherBadge}
                                  style={{
                                    borderColor: getVoucherProgressColor(
                                      o.voucher_response.required_responded,
                                      o.voucher_response.required_total
                                    ),
                                    backgroundColor: `${getVoucherProgressColor(
                                      o.voucher_response.required_responded,
                                      o.voucher_response.required_total
                                    )}15`,
                                    color: getVoucherProgressColor(
                                      o.voucher_response.required_responded,
                                      o.voucher_response.required_total
                                    ),
                                  }}>
                                  {intl.formatMessage({
                                    id: 'offers-core-required-label',
                                  })}
                                  : {o.voucher_response.required_responded}/
                                  {o.voucher_response.required_total}
                                </Tag>
                              </Tooltip>

                              <Tooltip
                                title={intl.formatMessage({
                                  id: 'offers-core-voucher-progress-tooltip',
                                })}>
                                <Tag
                                  css={styles.voucherBadge}
                                  style={{
                                    borderColor: getVoucherProgressColor(
                                      o.voucher_response.optional_responded,
                                      o.voucher_response.optional_total
                                    ),
                                    backgroundColor: `${getVoucherProgressColor(
                                      o.voucher_response.optional_responded,
                                      o.voucher_response.optional_total
                                    )}15`,
                                    color: getVoucherProgressColor(
                                      o.voucher_response.optional_responded,
                                      o.voucher_response.optional_total
                                    ),
                                  }}>
                                  {intl.formatMessage({
                                    id: 'offers-core-optional-label',
                                  })}
                                  : {o.voucher_response.optional_responded}/
                                  {o.voucher_response.optional_total}
                                </Tag>
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Optional: 'Click to close' at the bottom of the list */}
                  <div
                    css={[
                      styles.hintTextActive,
                      { marginTop: 8, textAlign: 'center' },
                    ]}
                    onClick={() => toggleExpanded(c.contract_id)}>
                    {intl.formatMessage({
                      id: 'click-to-close',
                    })}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  wrap: { display: 'flex', flexDirection: 'column', gap: 12, width: '100%' },
  list: { display: 'flex', flexDirection: 'column', gap: 10, width: '100%' },

  card: {
    borderRadius: 8,
    border: '1px solid #d9d9d9',
    '.ant-card-head': {
      minHeight: 'auto',
      padding: '0 12px',
    },
    '.ant-card-head-title': {
      padding: '10px 0',
    },
  },

  // --- Header Layout ---
  cardHeader: {
    display: 'flex',
    // alignItems: 'center', //
    flexDirection: 'column', // #REF & Name; Part. & Subm
    justifyContent: 'space-between',
    flexWrap: 'wrap', // Allow wrapping on very small screens
    gap: 8,
  },
  contractName: {
    fontWeight: 600,
    fontSize: 14,
    color: '#333',
  },
  chips: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  badgeWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  badgeLabel: {
    fontSize: 12,
    color: '#666',
  },

  // --- Hints (White Space) ---
  hintTextDisabled: {
    fontStyle: 'italic',
    color: '#999',
    fontSize: 12,
  },
  hintTextActive: {
    fontStyle: 'italic',
    color: '#1890ff',
    fontSize: 12,
    cursor: 'pointer',
    width: 'fit-content',
    '&:hover': {
      textDecoration: 'underline',
    },
  },

  // --- Offers List ---
  offersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 4,
  },

  // --- Individual Offer Item ---
  offerItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    padding: '10px 12px',
    border: '1px solid #f0f0f0',
    borderRadius: 6,
    backgroundColor: '#fff',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
  },

  // Generic Row utility for inside the offer
  offerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between', // Pushes items to edges
    fontSize: 12,
    color: '#555',
  },

  offerTitle: {
    fontWeight: 600,
    fontSize: 13,
    color: '#000',
  },

  offerInfo: {
    display: 'flex',
    alignItems: 'center',
  },

  label: {
    color: '#888',
    marginRight: 4,
  },

  // --- Voucher Response Styles ---
  voucherProgressBadges: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },

  voucherBadge: {
    margin: 0,
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 8px',
    border: '1px solid',
    borderRadius: 4,
  },
});
